import { FORECAST_MODELS } from '@app/common/static-data/models';
import { isJuaModel } from './colorUtils';

/**
 * Helper function to create confidence interval area series
 */
function createConfidenceIntervalSeries(
  chartData: any[],
  variableJuaId: string,
  modelId: string,
  modelLabel: string,
  color: string,
  lowerQuantile: string,
  upperQuantile: string,
  opacity: string,
  emphasisOpacity: string,
  blurOpacity: string,
  zIndex: number,
) {
  // Create the lower and upper bound data
  const lowerBoundData = chartData
    .map((point) => {
      const lowerValue = point[`${variableJuaId}_model_${modelId}_${lowerQuantile}`];
      const timeValue =
        typeof point.time === 'number' ? point.time : new Date(point.time).getTime();
      return lowerValue !== undefined ? [timeValue, lowerValue] : null;
    })
    .filter((point) => point !== null);

  const upperBoundData = chartData
    .map((point) => {
      const upperValue = point[`${variableJuaId}_model_${modelId}_${upperQuantile}`];
      const timeValue =
        typeof point.time === 'number' ? point.time : new Date(point.time).getTime();
      return upperValue !== undefined ? [timeValue, upperValue] : null;
    })
    .filter((point) => point !== null);

  // Create a single area series that fills between bounds
  const combinedData = lowerBoundData.concat(upperBoundData.reverse());

  return {
    name: modelLabel, // Same name as main series for linked highlighting
    type: 'line',
    data: combinedData,
    lineStyle: { width: 0, opacity: 0 },
    symbol: 'none',
    areaStyle: {
      color: `${color}${opacity}`,
    },
    showInLegend: false, // Don't show in legend since main series already does
    silent: true, // Prevent this series from intercepting mouse events
    tooltip: { show: false },
    emphasis: {
      focus: 'series' as const, // Link to main series highlighting
      areaStyle: {
        color: `${color}${emphasisOpacity}`, // Slightly more opaque when emphasized
      },
    },
    blur: {
      areaStyle: {
        color: `${color}${blurOpacity}`, // Much lower opacity when blurred
      },
    },
    z: zIndex,
  };
}

/**
 * Determine the minimum allowed value for confidence interval lower bound based on variable type
 */
function getMinAllowedValue(variableJuaId: string): number {
  // Variables that can have negative values
  if (
    variableJuaId === 'air_temperature_at_height_level_2m' ||
    variableJuaId === 'dew_point_temperature_at_height_level_2m'
  ) {
    return -273.15; // Absolute zero in Celsius (theoretical minimum temperature)
  }

  if (variableJuaId === 'geopotential_at_pressure_level_50000Pa') {
    return -1000; // Allow some negative values for geopotential (below sea level reference)
  }

  // All other variables (wind speeds, wind directions, air pressure, solar radiation) should be >= 0
  return 0;
}

/**
 * Generate mean and confidence interval series for ensemble forecasts
 */
export function generateEnsembleSeries(
  chartData: any[],
  sortedModels: string[],
  variableJuaId: string,
) {
  let meanSeries: any[] = [];
  let ciSeries: any[] = [];

  if (sortedModels.length <= 1) {
    return { meanSeries, ciSeries };
  }

  // Calculate mean and 2*std confidence interval for each time point, masking out points with insufficient data
  const meanData: [number, number | undefined][] = [];
  const ciUpper: [number, number | undefined][] = [];
  const ciLower: [number, number | undefined][] = [];

  chartData.forEach((point) => {
    const timeValue = typeof point.time === 'number' ? point.time : new Date(point.time).getTime();
    const values = sortedModels
      .map((modelId) => point[`${variableJuaId}_model_${modelId}`])
      .filter((val) => val !== undefined && val !== null && !isNaN(val));
    if (values.length >= 2) {
      // Only show band if at least 2 models have data
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const std = Math.sqrt(values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length);
      const minAllowed = getMinAllowedValue(variableJuaId);
      meanData.push([timeValue, mean]);
      ciLower.push([timeValue, Math.max(mean - 2 * std, minAllowed)]);
      ciUpper.push([timeValue, Math.max(mean + 2 * std, minAllowed)]);
    } else {
      meanData.push([timeValue, undefined]);
      ciUpper.push([timeValue, undefined]);
      ciLower.push([timeValue, undefined]);
    }
  });

  // Confidence interval area using the same approach as createConfidenceIntervalSeries
  const lowerBoundData = ciLower
    .map(([t, lower]) => (lower !== undefined ? [t, lower] : null))
    .filter((point) => point !== null);

  const upperBoundData = ciUpper
    .map(([t, upper]) => (upper !== undefined ? [t, upper] : null))
    .filter((point) => point !== null);

  // Create a single area series that fills between bounds (same as createConfidenceIntervalSeries)
  const combinedData = lowerBoundData.concat(upperBoundData.reverse());

  ciSeries = [];

  if (combinedData.length > 0) {
    ciSeries = [
      {
        name: 'Confidence Band',
        type: 'line',
        data: combinedData,
        lineStyle: { width: 0, opacity: 0 },
        symbol: 'none',
        areaStyle: {
          color: 'rgba(236, 72, 153, 0.20)',
        },
        showInLegend: false,
        silent: true,
        tooltip: { show: false },
        emphasis: { disabled: true },
        z: 1,
      },
    ];
  }

  // Mean line only
  meanSeries = [
    {
      name: 'Mean',
      type: 'line',
      data: meanData,
      lineStyle: { width: 2, color: '#EC4899' }, // pink
      itemStyle: { color: '#EC4899', opacity: 1 }, // Ensure pink color is also applied to itemStyle for tooltips
      symbol: 'none',
      z: 2,
    },
  ];

  return { meanSeries, ciSeries };
}

/**
 * Generate model series with proper styling and opacity, including confidence intervals
 */
export function generateModelSeries(
  sortedModels: string[],
  chartData: any[],
  colors: string[],
  variableJuaId: string,
  showMeanAndConfidence: boolean,
) {
  const modelOpacity = showMeanAndConfidence ? 0.15 : 1;
  const blurOpacity = showMeanAndConfidence ? modelOpacity : 0.15;

  // Check if this is a precipitation variable
  const isPrecipitation = variableJuaId === 'precipitation_amount_sum_1h';

  const series: any[] = [];

  sortedModels.forEach((modelId, index) => {
    const color = colors[index];
    const modelLabel = FORECAST_MODELS.find((m) => m.id === modelId)?.label || modelId;

    // Check if this model has confidence interval data
    const hasConfidenceData = chartData.some(
      (point) =>
        point[`${variableJuaId}_model_${modelId}_q5`] !== undefined &&
        point[`${variableJuaId}_model_${modelId}_q95`] !== undefined,
    );

    // Check if this model has 50% confidence interval data (q25, q75)
    const has50ConfidenceData = chartData.some(
      (point) =>
        point[`${variableJuaId}_model_${modelId}_q25`] !== undefined &&
        point[`${variableJuaId}_model_${modelId}_q75`] !== undefined,
    );

    // Configure series type and styling based on variable type
    const seriesConfig = isPrecipitation
      ? {
          type: 'bar' as const,
          barGap: '10%', // Small gap between bars of different series for better separation
          barCategoryGap: '20%', // Gap between bar categories for better readability
          // No fixed width constraints - let ECharts auto-calculate based on zoom level
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: `${color}CC` }, // 80% opacity at top - brighter
                { offset: 1, color: `${color}99` }, // 60% opacity at bottom - still bright
              ],
            },
            borderRadius: [4, 4, 0, 0],
            borderColor: `${color}CC`, // Same as top gradient for consistency
            borderWidth: 2, // Thicker border to make it more prominent
            opacity: modelOpacity,
          },
          emphasis: {
            focus: 'series' as const,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: `${color}FF` }, // Full opacity at top when emphasized
                  { offset: 1, color: `${color}DD` }, // 87% opacity at bottom when emphasized
                ],
              },
              borderRadius: [4, 4, 0, 0],
              borderColor: `${color}FF`, // Full opacity border for maximum emphasis
              borderWidth: 3, // Even thicker border on emphasis
              opacity: 1,
            },
          },
          blur: {
            itemStyle: {
              opacity: blurOpacity,
            },
          },
          animationDuration: 800,
          animationEasing: 'elasticOut',
        }
      : {
          type: 'line' as const,
          showSymbol: false,
          connectNulls: false,
          lineStyle: {
            width: isJuaModel(modelId) ? 2 : 1.5,
            opacity: modelOpacity,
            color: color,
          },
          itemStyle: {
            color: color,
            opacity: 1,
          },
          emphasis: {
            focus: 'series' as const,
            lineStyle: {
              width: 3,
              opacity: 1,
              color: color,
            },
            itemStyle: {
              opacity: 1,
              color: color,
            },
          },
          blur: {
            lineStyle: {
              opacity: blurOpacity,
            },
            itemStyle: {
              opacity: blurOpacity,
            },
          },
        };

    // Add the main series
    series.push({
      name: modelLabel,
      ...seriesConfig,
      data: chartData
        .map((point) => {
          const value = point[`${variableJuaId}_model_${modelId}`];
          const q5Value = point[`${variableJuaId}_model_${modelId}_q5`];
          const q95Value = point[`${variableJuaId}_model_${modelId}_q95`];
          const q25Value = point[`${variableJuaId}_model_${modelId}_q25`];
          const q75Value = point[`${variableJuaId}_model_${modelId}_q75`];

          if (value !== undefined) {
            // Create data point with confidence interval metadata
            const timeValue =
              typeof point.time === 'number' ? point.time : new Date(point.time).getTime();
            const dataPoint: any = {
              value: [timeValue, value],
              q5: q5Value,
              q95: q95Value,
              q25: q25Value,
              q75: q75Value,
            };
            return dataPoint;
          }
          return null;
        })
        .filter((point) => point !== null),
      z: 20,
    });

    // Add confidence interval areas if data is available (only for line charts, not bars)
    if (hasConfidenceData && !isPrecipitation) {
      series.push(
        createConfidenceIntervalSeries(
          chartData,
          variableJuaId,
          modelId,
          modelLabel,
          color,
          'q5',
          'q95',
          '30', // 30% opacity
          '40', // Slightly more opaque when emphasized
          '10', // Much lower opacity when blurred
          1,
        ),
      );
    }

    // Add 50% confidence interval area if data is available (only for line charts, not bars)
    if (has50ConfidenceData && !isPrecipitation) {
      series.push(
        createConfidenceIntervalSeries(
          chartData,
          variableJuaId,
          modelId,
          modelLabel,
          color,
          'q25',
          'q75',
          '50', // 50% opacity (darker than 90% CI)
          '60', // Slightly more opaque when emphasized
          '20', // Much lower opacity when blurred
          2,
        ),
      );
    }
  });

  return series;
}
